<div class="password-management-container">
  <!-- Password Action Buttons -->
  <div class="password-actions">
    <!-- Password Policy Warning -->
    <div class="alert alert-warning" *ngIf="passwordPolicyMessage" style="margin-bottom: 15px; padding: 10px; border-radius: 5px; background-color: #fff3cd; border: 1px solid #ffeaa7; color: #856404;">
      <i class="fas fa-exclamation-triangle"></i> {{ passwordPolicyMessage }}
    </div>
    
    <button 
      type="button" 
      class="btn btn-link password-action-btn"
      (click)="toggleResetPassword()"
      [class.active]="showResetPassword"
      [disabled]="mustChangePassword">
      <i class="fas fa-unlock-alt"></i>
      Forgot Password?
    </button>
    
    <button 
      type="button" 
      class="btn btn-link password-action-btn"
      (click)="toggleUpdatePassword()"
      [class.active]="showUpdatePassword">
      <i class="fas fa-key"></i>
      {{ mustChangePassword ? 'Change Password (Required)' : 'Update Password' }}
    </button>
  </div>

  <!-- Reset Password Form -->
  <div class="password-form-container" *ngIf="showResetPassword">
    <div class="form-card">
      <div class="form-header">
        <h4><i class="fas fa-unlock-alt"></i> Reset Password</h4>
        <button type="button" class="close-btn" (click)="showResetPassword = false">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <form [formGroup]="resetPasswordForm" (ngSubmit)="onResetPassword()">
        <div class="form-group">
          <label for="resetEmail">Email Address</label>
          <div class="input-group">
            <span class="input-group-text">
              <i class="fas fa-envelope"></i>
            </span>
            <input
              type="email"
              id="resetEmail"
              class="form-control"
              formControlName="email"
              placeholder="Enter your email address"
              [class.is-invalid]="resetEmailControl?.invalid && resetEmailControl?.touched">
          </div>
          <div class="invalid-feedback" *ngIf="resetEmailControl?.invalid && resetEmailControl?.touched">
            <span *ngIf="resetEmailControl?.errors?.['required']">Email is required</span>
            <span *ngIf="resetEmailControl?.errors?.['email']">Please enter a valid email</span>
          </div>
        </div>

        <div class="form-group">
          <label for="resetUsername">Username</label>
          <div class="input-group">
            <span class="input-group-text">
              <i class="fas fa-user"></i>
            </span>
            <input
              type="text"
              id="resetUsername"
              class="form-control"
              formControlName="username"
              placeholder="Enter your username"
              [class.is-invalid]="resetUsernameControl?.invalid && resetUsernameControl?.touched">
          </div>
          <div class="invalid-feedback" *ngIf="resetUsernameControl?.invalid && resetUsernameControl?.touched">
            <span *ngIf="resetUsernameControl?.errors?.['required']">Username is required</span>
          </div>
        </div>

        <div class="form-actions">
          <button 
            type="submit" 
            class="btn btn-primary btn-block"
            [disabled]="isLoading || resetPasswordForm.invalid">
            <span *ngIf="isLoading" class="spinner-border spinner-border-sm me-2"></span>
            <i class="fas fa-paper-plane" *ngIf="!isLoading"></i>
            {{ isLoading ? 'Sending...' : 'Send Reset Link' }}
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Update Password Form -->
  <div class="password-form-container" *ngIf="showUpdatePassword">
    <div class="form-card">
      <div class="form-header">
        <h4><i class="fas fa-key"></i> {{ mustChangePassword ? 'Change Password (Required)' : 'Update Password' }}</h4>
        <button type="button" class="close-btn" (click)="toggleUpdatePassword()" [disabled]="mustChangePassword">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <form [formGroup]="updatePasswordForm" (ngSubmit)="onUpdatePassword()">
        <div class="form-group">
          <label for="updateUsername">Username</label>
          <div class="input-group">
            <span class="input-group-text">
              <i class="fas fa-user"></i>
            </span>
            <input
              type="text"
              id="updateUsername"
              class="form-control"
              formControlName="username"
              placeholder="Enter your username"
              [class.is-invalid]="updateUsernameControl?.invalid && updateUsernameControl?.touched">
          </div>
          <div class="invalid-feedback" *ngIf="updateUsernameControl?.invalid && updateUsernameControl?.touched">
            <span *ngIf="updateUsernameControl?.errors?.['required']">Username is required</span>
            <span *ngIf="updateUsernameControl?.errors?.['invalidUsername']">Username not found or invalid</span>
          </div>
        </div>

        <div class="form-group">
          <label for="currentPassword">Current Password</label>
          <div class="input-group">
            <span class="input-group-text">
              <i class="fas fa-lock"></i>
            </span>
            <input
              type="password"
              id="currentPassword"
              class="form-control"
              formControlName="current_password"
              placeholder="Enter current password"
              [class.is-invalid]="currentPasswordControl?.invalid && currentPasswordControl?.touched">
          </div>
          <div class="invalid-feedback" *ngIf="currentPasswordControl?.invalid && currentPasswordControl?.touched">
            <span *ngIf="currentPasswordControl?.errors?.['required']">Current password is required</span>
            <span *ngIf="currentPasswordControl?.errors?.['minlength']">Password must be at least 6 characters</span>
            <span *ngIf="currentPasswordControl?.errors?.['incorrectPassword']">Current password is incorrect</span>
          </div>
        </div>

        <div class="form-group">
          <label for="newPassword">New Password</label>
          <div class="input-group">
            <span class="input-group-text">
              <i class="fas fa-key"></i>
            </span>
            <input
              type="password"
              id="newPassword"
              class="form-control"
              formControlName="updated_password"
              placeholder="Enter new password"
              [class.is-invalid]="updatedPasswordControl?.invalid && updatedPasswordControl?.touched">
          </div>
          
          <!-- Password Strength Requirements -->
          <div class="password-requirements" *ngIf="updatedPasswordControl?.touched" style="margin-top: 8px;">
            <small class="text-muted"><strong>Password Requirements:</strong></small>
            <ul class="requirements-list" style="font-size: 12px; margin: 5px 0; padding-left: 20px;">
              <li [style.color]="!updatedPasswordControl?.errors?.['minLength'] ? 'green' : 'red'">
                <i [class]="!updatedPasswordControl?.errors?.['minLength'] ? 'fas fa-check' : 'fas fa-times'"></i>
                At least 8 characters
              </li>
              <li [style.color]="!updatedPasswordControl?.errors?.['noUpperCase'] ? 'green' : 'red'">
                <i [class]="!updatedPasswordControl?.errors?.['noUpperCase'] ? 'fas fa-check' : 'fas fa-times'"></i>
                One uppercase letter
              </li>
              <li [style.color]="!updatedPasswordControl?.errors?.['noLowerCase'] ? 'green' : 'red'">
                <i [class]="!updatedPasswordControl?.errors?.['noLowerCase'] ? 'fas fa-check' : 'fas fa-times'"></i>
                One lowercase letter
              </li>
              <li [style.color]="!updatedPasswordControl?.errors?.['noNumber'] ? 'green' : 'red'">
                <i [class]="!updatedPasswordControl?.errors?.['noNumber'] ? 'fas fa-check' : 'fas fa-times'"></i>
                One number
              </li>
              <li [style.color]="!updatedPasswordControl?.errors?.['noSpecialChar'] ? 'green' : 'red'">
                <i [class]="!updatedPasswordControl?.errors?.['noSpecialChar'] ? 'fas fa-check' : 'fas fa-times'"></i>
                One special character
              </li>
              <li [style.color]="!updatedPasswordControl?.errors?.['sameAsOld'] ? 'green' : 'red'">
                <i [class]="!updatedPasswordControl?.errors?.['sameAsOld'] ? 'fas fa-check' : 'fas fa-times'"></i>
                Different from current password
              </li>
            </ul>
          </div>
          
          <div class="invalid-feedback" *ngIf="updatedPasswordControl?.invalid && updatedPasswordControl?.touched">
            <span *ngIf="updatedPasswordControl?.errors?.['required']">New password is required</span>
            <span *ngIf="passwordStrengthErrors.length > 0">Please satisfy all password requirements above</span>
          </div>
        </div>

        <div class="form-group">
          <label for="confirmPassword">Confirm New Password</label>
          <div class="input-group">
            <span class="input-group-text">
              <i class="fas fa-check-circle"></i>
            </span>
            <input
              type="password"
              id="confirmPassword"
              class="form-control"
              formControlName="confirm_updated_password"
              placeholder="Confirm new password"
              [class.is-invalid]="confirmPasswordControl?.invalid && confirmPasswordControl?.touched">
          </div>
          <div class="invalid-feedback" *ngIf="confirmPasswordControl?.invalid && confirmPasswordControl?.touched">
            <span *ngIf="confirmPasswordControl?.errors?.['required']">Please confirm your password</span>
            <span *ngIf="confirmPasswordControl?.errors?.['passwordMismatch']">Passwords do not match</span>
          </div>
        </div>

        <div class="form-actions">
          <button 
            type="submit" 
            class="btn btn-primary btn-block"
            [disabled]="isLoading || updatePasswordForm.invalid">
            <span *ngIf="isLoading" class="spinner-border spinner-border-sm me-2"></span>
            <i class="fas fa-save" *ngIf="!isLoading"></i>
            {{ isLoading ? 'Updating...' : 'Update Password' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</div>